// Public, read-only WordPress client for the headless blog.
// Publishing credentials must never be shipped to the browser.
import { BLOG_FALLBACK_COVER, stripHtml } from '../utils/seo';

const WORDPRESS_URL = process.env.REACT_APP_WORDPRESS_URL || 'https://cms.sistakyerewaa.com';

const decodeHtmlEntities = (text = '') => text
  .replace(/&quot;/g, '"')
  .replace(/&#8220;/g, '"')
  .replace(/&#8221;/g, '"')
  .replace(/&#8217;/g, "'")
  .replace(/&#8216;/g, "'")
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&#8230;/g, '...')
  .replace(/&#8211;/g, '–')
  .replace(/&#8212;/g, '—')
  .replace(/&#038;/g, '&');

class WordPressAPI {
  constructor() {
    this.baseURL = `${WORDPRESS_URL}/wp-json/wp/v2`;
  }

  async request(path) {
    const response = await fetch(`${this.baseURL}${path}`);
    if (!response.ok) throw new Error(`WordPress request failed (${response.status})`);
    return response.json();
  }

  async getAllPosts() {
    try {
      const posts = await this.request('/posts?_embed&per_page=100&status=publish');
      return posts.map((post) => this.transformPost(post));
    } catch (error) {
      console.error('Unable to load WordPress posts:', error);
      return [];
    }
  }

  async getPostBySlug(slug) {
    try {
      const posts = await this.request(`/posts?slug=${encodeURIComponent(slug)}&_embed&status=publish`);
      return posts[0] ? this.transformPost(posts[0]) : null;
    } catch (error) {
      console.error('Unable to load WordPress post:', error);
      return null;
    }
  }

  async searchPosts(query) {
    try {
      const posts = await this.request(`/posts?search=${encodeURIComponent(query)}&_embed&per_page=100&status=publish`);
      return posts.map((post) => this.transformPost(post));
    } catch (error) {
      console.error('Unable to search WordPress posts:', error);
      return [];
    }
  }

  transformPost(wpPost) {
    const embeddedTerms = wpPost._embedded?.['wp:term'] || [];
    const tags = [...new Set(embeddedTerms.flat().map((term) => term.name).filter(Boolean))];
    const content = decodeHtmlEntities(wpPost.content?.rendered || '');
    const excerpt = decodeHtmlEntities(stripHtml(wpPost.excerpt?.rendered || ''));
    const featuredImage = wpPost._embedded?.['wp:featuredmedia']?.[0]?.source_url;
    const imageInContent = content.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1];

    return {
      id: wpPost.id,
      title: decodeHtmlEntities(wpPost.title?.rendered || 'Untitled post'),
      slug: wpPost.slug,
      date: wpPost.date,
      modified: wpPost.modified,
      excerpt,
      content,
      cover: featuredImage || imageInContent || BLOG_FALLBACK_COVER,
      tags,
      author: wpPost._embedded?.author?.[0]?.name || 'Sista Kyerewaa',
    };
  }
}

const wordpressApi = new WordPressAPI();

export default wordpressApi;
